import React, { useId, useMemo } from "react";

import { getCatalogMediaRoles, normalizeMediaRole } from "../../../../utils/catalogProductStudioShared";
import { fill, getVariantColorValue, getVariantLabel, isFilledMedia, mediaCanCoverColor } from "../studioEditModel";
import { Btn, Drawer, Field, Icon, Select, Swatch, TextInput, cx } from "../StudioUi";
import { mediaDraftActions } from "../mediaDraftActions";

const ROLE_KEYS = {
  HERO: "mdRoleHero",
  GALLERY: "mdRoleGallery",
  DETAIL: "mdRoleDetail",
  TEXTURE: "mdRoleTexture",
  MODEL: "mdRoleModel",
  PACKAGING: "mdRolePackaging",
  LIFESTYLE: "mdRoleLifestyle",
  OTHER: "mdRoleOther",
};

/**
 * Everything about one photo: preview, order, product cover, colour covers,
 * variant links, alt text and role. Edits go to the drafts and are saved with
 * "Ruaj ndryshimet", exactly like the previous media editor.
 */
const ImageDetailsDrawer = ({ studio, index, onClose, onNavigate, onRequestRemove }) => {
  const { tt, t, mediaDrafts, setMediaDrafts, productColors, colorRecords, liveVariants, colorOption } = studio;
  const ids = useId();
  const item = index !== null ? mediaDrafts[index] : null;
  const roles = useMemo(() => getCatalogMediaRoles(t), [t]);

  const filledIndexes = mediaDrafts.map((entry, entryIndex) => (isFilledMedia(entry) ? entryIndex : null)).filter(
    (entryIndex) => entryIndex !== null
  );
  const position = filledIndexes.indexOf(index) + 1;

  // Variants grouped by colour (or a single group when there is no colour).
  const variantGroups = useMemo(() => {
    if (!productColors.length) return [{ key: "all", color: null, variants: liveVariants }];
    return productColors.map((color) => ({
      key: color.optionValueId,
      color,
      variants: liveVariants.filter(
        (variant) => getVariantColorValue(variant, colorOption)?.optionValueId === color.optionValueId
      ),
    }));
  }, [colorOption, liveVariants, productColors]);

  if (!item) return null;

  const selected = new Set((item.variantIds || []).map(Number));
  const update = (field, value) => setMediaDrafts((items) => mediaDraftActions.update(items, index, field, value));
  const setVariantIds = (variantIds) =>
    setMediaDrafts((items) => mediaDraftActions.setVariantIds(items, index, variantIds, productColors));
  const toggleVariant = (variantId) => {
    const next = new Set(selected);
    if (next.has(variantId)) next.delete(variantId);
    else next.add(variantId);
    setVariantIds(Array.from(next));
  };
  const toggleGroup = (variants, shouldSelect) => {
    const next = new Set(selected);
    variants.forEach((variant) => (shouldSelect ? next.add(variant.id) : next.delete(variant.id)));
    setVariantIds(Array.from(next));
  };

  const previousIndex = filledIndexes[filledIndexes.indexOf(index) - 1];
  const nextIndex = filledIndexes[filledIndexes.indexOf(index) + 1];

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={tt("mdImageDetails")}
      description={fill(tt("mdPositionOf"), { n: position, total: filledIndexes.length })}
      icon="image"
      size="lg"
      closeLabel={tt("close")}
      footer={
        <>
          <Btn variant="danger-ghost" icon="trash" onClick={() => onRequestRemove(index)}>
            {tt("mdRemovePhoto")}
          </Btn>
          <Btn variant="primary" onClick={onClose}>
            {tt("done")}
          </Btn>
        </>
      }
    >
      <div className="pse-image-detail">
        <div className="pse-image-detail__preview">
          {item.sourceUrl ? <img src={item.sourceUrl} alt={item.altText || ""} /> : <span>{tt("mdPreviewUnavailable")}</span>}
          <div className="pse-image-detail__nav">
            <Btn
              variant="secondary"
              size="sm"
              icon="chevronLeft"
              disabled={previousIndex === undefined}
              onClick={() => onNavigate(previousIndex)}
              aria-label={tt("moveEarlier")}
            />
            <Btn
              variant="secondary"
              size="sm"
              icon="chevronRight"
              disabled={nextIndex === undefined}
              onClick={() => onNavigate(nextIndex)}
              aria-label={tt("moveLater")}
            />
          </div>
        </div>

        <div className="pse-image-detail__row">
          <Btn
            variant={item.isCover ? "success-soft" : "secondary"}
            icon="star"
            disabled={item.isCover}
            onClick={() => setMediaDrafts((items) => mediaDraftActions.setProductCover(items, index))}
          >
            {item.isCover ? tt("mdProductCoverLong") : tt("mdSetProductCover")}
          </Btn>
          <Btn
            variant="secondary"
            icon="chevronLeft"
            disabled={index === 0}
            onClick={() => {
              setMediaDrafts((items) => mediaDraftActions.move(items, index, -1));
              onNavigate(index - 1);
            }}
          >
            {tt("moveEarlier")}
          </Btn>
          <Btn
            variant="secondary"
            iconRight="chevronRight"
            disabled={index === mediaDrafts.length - 1}
            onClick={() => {
              setMediaDrafts((items) => mediaDraftActions.move(items, index, 1));
              onNavigate(index + 1);
            }}
          >
            {tt("moveLater")}
          </Btn>
        </div>

        {productColors.length ? (
          <section className="pse-drawer-section">
            <h3 className="pse-drawer-section__title">{tt("mdColorCoverSection")}</h3>
            <p className="pse-muted pse-small">{tt("mdColorCoverSectionHint")}</p>
            <div className="pse-color-cover-grid">
              {productColors.map((color) => {
                const isCover = (item.colorCoverOptionValueIds || []).includes(color.optionValueId);
                const eligible = mediaCanCoverColor(item, color);
                return (
                  <button
                    key={color.optionValueId}
                    type="button"
                    className={cx("pse-color-cover-option", isCover && "is-selected")}
                    aria-pressed={isCover}
                    disabled={!eligible && !isCover}
                    title={eligible ? undefined : fill(tt("mdColorCoverIneligible"), { color: color.value })}
                    onClick={() =>
                      setMediaDrafts((items) =>
                        mediaDraftActions.toggleColorCover(items, index, color.optionValueId, !isCover)
                      )
                    }
                  >
                    <Swatch value={color.value} colorRecords={colorRecords} size={18} />
                    <span>{color.value}</span>
                    {isCover ? <Icon name="check" size={16} strokeWidth={2.4} /> : null}
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {liveVariants.length > 1 ? (
          <section className="pse-drawer-section">
            <h3 className="pse-drawer-section__title">{tt("mdVariantsLink")}</h3>
            <p className="pse-muted pse-small">
              {tt("mdVariantsLinkHint")}{" "}
              <strong>
                {selected.size ? fill(tt("mdVariantsCount"), { count: selected.size }) : tt("mdVariantsAll")}
              </strong>
            </p>
            {variantGroups.map((group) => {
              const allSelected = group.variants.length > 0 && group.variants.every((variant) => selected.has(variant.id));
              return (
                <div key={group.key} className="pse-variant-link-group">
                  {group.color ? (
                    <div className="pse-variant-link-group__head">
                      <span className="pse-variant-link-group__name">
                        <Swatch value={group.color.value} colorRecords={colorRecords} size={16} />
                        {group.color.value}
                      </span>
                      <button
                        type="button"
                        className="pse-link"
                        onClick={() => toggleGroup(group.variants, !allSelected)}
                      >
                        {allSelected ? tt("mdClearColor") : tt("mdSelectColor")}
                      </button>
                    </div>
                  ) : null}
                  <div className="pse-check-grid">
                    {group.variants.map((variant) => (
                      <label key={variant.id} className={cx("pse-check-pill", selected.has(variant.id) && "is-checked")}>
                        <input
                          type="checkbox"
                          checked={selected.has(variant.id)}
                          onChange={() => toggleVariant(variant.id)}
                        />
                        <span>{getVariantLabel(variant) || fill(tt("variantFallback"), { id: variant.id })}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        ) : null}

        <section className="pse-drawer-section">
          <Field
            label={tt("mdAltText")}
            htmlFor={`${ids}-alt`}
            hint={tt("mdAltHint")}
            counter={String((item.altText || "").length)}
          >
            <TextInput
              id={`${ids}-alt`}
              value={item.altText}
              placeholder={tt("mdAltPlaceholder")}
              onChange={(value) => update("altText", value)}
            />
          </Field>
          <Field label={tt("mdRole")} htmlFor={`${ids}-role`}>
            <Select id={`${ids}-role`} value={normalizeMediaRole(item.role)} onChange={(value) => update("role", value)}>
              {roles.map((role) => (
                <option key={role.value} value={role.value}>
                  {ROLE_KEYS[role.value] ? tt(ROLE_KEYS[role.value]) : role.label}
                </option>
              ))}
            </Select>
          </Field>
        </section>
      </div>
    </Drawer>
  );
};

export default ImageDetailsDrawer;
